import type { AppPermissionRegistryConfig, AppName } from '../types';

class PermissionRegistry<TResources extends string = string> {
  private static instances = new Map<string, PermissionRegistry>();

  private static currentApp: AppName | null = null;

  private config: AppPermissionRegistryConfig<TResources> | null = null;

  static getInstance<TResources extends string = string>(appName: AppName): PermissionRegistry<TResources> {
    if (!this.instances.has(appName)) {
      this.instances.set(appName, new PermissionRegistry<TResources>());
    }
    return this.instances.get(appName) as PermissionRegistry<TResources>;
  }

  static register<TResources extends string>(config: AppPermissionRegistryConfig<TResources>): void {
    const registry = new PermissionRegistry<TResources>();
    registry.config = config;
    this.instances.set(config.appName, registry);
  }

  static setCurrentApp(appName: AppName): void {
    this.currentApp = appName;
  }

  static getCurrentApp(): AppName | null {
    return this.currentApp;
  }

  getConfig(): AppPermissionRegistryConfig<TResources> | undefined {
    return this.config ?? undefined;
  }

  getResources(): (TResources | string)[] {
    if (!this.config) return [];
    return [...Object.keys(this.config.resources), ...Object.keys(this.config.pages)];
  }

  getActions(resource: string): string[] {
    if (!this.config) return [];
    const internal = this.config.resourceAliases?.[resource] ?? resource;
    const resourceConfig = this.config.resources[internal as TResources];
    return resourceConfig?.actions ? [...resourceConfig.actions] : [];
  }

  getAllResources(): (TResources | string)[] {
    return this.getResources();
  }

  getAllActions(resource: string): string[] {
    return this.getActions(resource);
  }

  getInternalName(apiResource: string): TResources | undefined {
    return this.config?.resourceAliases?.[apiResource];
  }

  getApiResourceName(internalResource: TResources | string): string {
    if (!this.config) return String(internalResource);
    const aliasEntry = Object.entries(this.config.resourceAliases ?? {}).find(
      ([, internal]) => internal === internalResource,
    );
    return aliasEntry ? aliasEntry[0] : String(internalResource);
  }

  hasResource(resource: string): boolean {
    if (!this.config) return true;
    const internal = this.config.resourceAliases?.[resource] ?? resource;
    return internal in this.config.resources || internal in this.config.pages;
  }

  isPage(resource: string): boolean {
    if (!this.config) return false;
    const internal = this.config.resourceAliases?.[resource] ?? resource;
    return internal in this.config.pages;
  }

  getResourceLabel(resource: string): string {
    if (!this.config) return String(resource);
    const internal = this.config.resourceAliases?.[resource] ?? resource;
    // Check resources first
    if (internal in this.config.resources) {
      return this.config.resources[internal as TResources]?.label ?? String(resource);
    }
    // Check pages
    if (internal in this.config.pages) {
      return this.config.pages[internal]?.label ?? String(resource);
    }
    return String(resource);
  }

  getActionLabel(resource: string, action: string): string {
    if (!this.config) return action;
    const internal = this.config.resourceAliases?.[resource] ?? resource;
    const resourceConfig = this.config.resources[internal as TResources];
    return resourceConfig?.actionLabels?.[action] ?? action;
  }

  // eslint-disable-next-line class-methods-use-this
  validateResource(_resource: string): boolean {
    return true;
  }
}

export { PermissionRegistry };
